'use client'

import React, { useMemo, useState } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import { formatCampTitleYearCategory } from '@/utilities/campDisplayLabel'
import { filterSelectableCamps } from '@/utilities/selectableCamps'
import { EditIcon } from 'lucide-react'
import Link from 'next/link'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

type Child = {
  name: string
  birthday: string | undefined
  needsLiftTicket: boolean
  needsEquipment: boolean
}

interface StatusManagementDialogProps {
  isOpen: boolean
  onClose: () => void
  anmeldung: PopulatedAnmeldung | null
  onStatusUpdate: (
    anmeldungId: string,
    newStatus: string,
    notes?: string,
    alternativeCampId?: string,
    children?: Child[],
  ) => Promise<void>
  onCampChange?: (anmeldungId: string, newCampId: string, notes?: string) => Promise<void>
}

const statusOptions = [
  { value: 'submitted', label: 'Eingereicht', color: 'bg-amber-100 text-amber-800' },
  { value: 'confirmed', label: 'Bestätigt', color: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Abgelehnt', color: 'bg-red-100 text-red-800' },
  { value: 'waitlist', label: 'Warteliste', color: 'bg-gray-100 text-gray-700' },
]

// Helper function to calculate age from birthday
const calculateAge = (birthday: string): number => {
  if (!birthday) return 0
  const today = new Date()
  const birthDate = new Date(birthday)
  return Math.floor((today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
}

export const StatusManagementDialog: React.FC<StatusManagementDialogProps> = ({
  isOpen,
  onClose,
  anmeldung,
  onStatusUpdate,
  onCampChange,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [availableCamps, setAvailableCamps] = useState<Camp[]>([])
  const [selectedCampId, setSelectedCampId] = useState<string>('')
  const [selectedAlternativeCampId, setSelectedAlternativeCampId] = useState<string>('')
  const [showPastCamps, setShowPastCamps] = useState(false)
  const [showCampChangeConfirmation, setShowCampChangeConfirmation] = useState(false)
  const [campChangeNotes, setCampChangeNotes] = useState<string>('')
  const [children, setChildren] = useState<Child[]>([])
  const [editingChildIndex, setEditingChildIndex] = useState<number | null>(null)
  const [showChildForm, setShowChildForm] = useState(false)
  const [childForm, setChildForm] = useState<Child>({
    name: '',
    birthday: '',
    needsLiftTicket: false,
    needsEquipment: false,
  })
  const [isLoadingPaymentStatus, setIsLoadingPaymentStatus] = useState(false)

  // Last season's camps are only offered on request; the current values stay visible
  const selectableCamps = useMemo(
    () =>
      filterSelectableCamps(availableCamps, {
        showPast: showPastCamps,
        keepIds: [anmeldung?.camp.id, selectedCampId, selectedAlternativeCampId],
      }),
    [availableCamps, showPastCamps, anmeldung, selectedCampId, selectedAlternativeCampId],
  )

  React.useEffect(() => {
    if (anmeldung) {
      setSelectedStatus(anmeldung.status || 'submitted')
      setNotes(anmeldung.adminNotes || '')
      setSelectedCampId(anmeldung.camp.id)
      // Handle alternativeCamp - it might be an object with id, or just an id string
      const alternativeCampId =
        typeof anmeldung.alternativeCamp === 'object' && anmeldung.alternativeCamp !== null
          ? anmeldung.alternativeCamp.id
          : typeof anmeldung.alternativeCamp === 'string'
            ? anmeldung.alternativeCamp
            : ''
      setSelectedAlternativeCampId(alternativeCampId)
      setChildren(
        (anmeldung.children || []).map((child) => ({
          name: child.name,
          birthday: child.birthday || '',
          needsLiftTicket: child.needsLiftTicket || false,
          needsEquipment: child.needsEquipment || false,
        })),
      )
    }
  }, [anmeldung])

  // Fetch available camps when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      fetchAvailableCamps()
    }
  }, [isOpen])

  const fetchAvailableCamps = async () => {
    try {
      const response = await fetch('/api/admin/camps?includePast=true')
      const data = await response.json()
      if (data.camps) {
        setAvailableCamps(data.camps)
      }
    } catch (error) {
      console.error('Error fetching camps:', error)
    }
  }

  const handleSave = async () => {
    if (!anmeldung || !selectedStatus) return

    setIsLoading(true)
    try {
      await onStatusUpdate(
        anmeldung.id,
        selectedStatus,
        notes,
        selectedAlternativeCampId || undefined,
        children,
      )
      onClose()
    } catch (error) {
      console.error('Error updating status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCampChange = () => {
    if (selectedCampId !== anmeldung?.camp.id) {
      setShowCampChangeConfirmation(true)
    }
  }

  const handleCampChangeConfirm = async () => {
    if (!anmeldung || !selectedCampId || !onCampChange) return

    setIsLoading(true)
    try {
      await onCampChange(anmeldung.id, selectedCampId, campChangeNotes)
      setShowCampChangeConfirmation(false)
      onClose()

      // Navigate to the new camp's view
      const url = new URL(window.location.href)
      url.searchParams.set('camp', selectedCampId)
      url.searchParams.delete('emailSearch') // Clear any email search when switching camps
      window.location.href = url.toString()
    } catch (error) {
      console.error('Error changing camp:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Children management functions
  const handleAddChild = () => {
    setChildForm({
      name: '',
      birthday: '',
      needsLiftTicket: false,
      needsEquipment: false,
    })
    setEditingChildIndex(null)
    setShowChildForm(true)
  }

  const handleEditChild = (index: number) => {
    const child = children[index]
    if (child) {
      // Ensure birthday is in YYYY-MM-DD format for date input
      const formattedBirthday = child.birthday
        ? new Date(child.birthday).toISOString().split('T')[0]
        : ''

      setChildForm({
        ...child,
        birthday: formattedBirthday,
      })
      setEditingChildIndex(index)
      setShowChildForm(true)
    }
  }

  const handleDeleteChild = (index: number) => {
    setChildren(children.filter((_, i) => i !== index))
  }

  const handleSaveChild = () => {
    if (!childForm.name.trim() || !childForm.birthday) return

    if (editingChildIndex !== null) {
      // Edit existing child
      const updatedChildren = [...children]
      updatedChildren[editingChildIndex] = childForm
      setChildren(updatedChildren)
    } else {
      // Add new child
      setChildren([...children, childForm])
    }

    setShowChildForm(false)
    setEditingChildIndex(null)
  }

  const handleCancelChild = () => {
    setShowChildForm(false)
    setEditingChildIndex(null)
  }

  const handleLogPaymentStatus = async () => {
    if (!anmeldung) return

    setIsLoadingPaymentStatus(true)
    try {
      const response = await fetch('/api/payments/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ registrationId: anmeldung.id }),
      })

      const data = await response.json()

      if (response.ok) {
        console.log('Payment status response:', data)
        alert(
          'Payment status wurde in der Konsole geloggt. Öffne die Entwicklertools (F12) um die Details zu sehen.',
        )
      } else {
        console.error('Error fetching payment status:', data.error)
        alert(`Fehler beim Abrufen des Zahlungsstatus: ${data.error}`)
      }
    } catch (error) {
      console.error('Error logging payment status:', error)
      alert('Fehler beim Abrufen des Zahlungsstatus')
    } finally {
      setIsLoadingPaymentStatus(false)
    }
  }

  if (!isOpen || !anmeldung) return null

  const selectedCampChangeTarget = availableCamps.find((c) => c.id === selectedCampId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-7xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">Anmeldung bearbeiten</h2>
        </div>

        {/* Content */}
        <div className="grid grid-cols-2 gap-6 p-6">
          <div className="mb-6 rounded-lg bg-gray-50 p-4">
            <h3 className="mb-6 font-medium text-gray-900">Teilnehmer-Informationen</h3>
            <div className="grid grid-cols-1 gap-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 font-medium">
                  {anmeldung.teilnehmer.firstName} {anmeldung.teilnehmer.lastName}{' '}
                  <Link href={`/admin/collections/camp-anmeldung/${anmeldung.id}`}>
                    <EditIcon className="h-4 w-4" />
                  </Link>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">E-Mail:</span>
                <span className="ml-2 font-medium">{anmeldung.teilnehmer.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Telefon:</span>
                <span className="ml-2 font-medium">
                  {anmeldung.teilnehmer.phone || 'Nicht angegeben'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Geburtstag:</span>
                <span className="ml-2 font-medium">
                  {anmeldung.teilnehmer.birthday
                    ? new Date(anmeldung.teilnehmer.birthday).toLocaleDateString('de-DE')
                    : 'Nicht angegeben'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Adresse:</span>
                <span className="ml-2 font-medium">
                  {anmeldung.teilnehmer.street}, {anmeldung.teilnehmer.plz}{' '}
                  {anmeldung.teilnehmer.city}, {anmeldung.teilnehmer.country}
                </span>
              </div>
              <div className="flex items-start justify-between gap-2">
                <span className="shrink-0 text-gray-500">Camp:</span>
                <span className="ml-2 text-right font-medium">
                  {formatCampTitleYearCategory(anmeldung.camp)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Aktueller Status:</span>
                <span
                  className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.status === 'confirmed'
                      ? 'bg-green-100 text-green-800'
                      : anmeldung.status === 'submitted'
                        ? 'bg-amber-100 text-amber-800'
                        : anmeldung.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : anmeldung.status === 'waitlist'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {anmeldung.status === 'confirmed'
                    ? 'Bestätigt'
                    : anmeldung.status === 'submitted'
                      ? 'Eingereicht'
                      : anmeldung.status === 'rejected'
                        ? 'Abgelehnt'
                        : anmeldung.status === 'waitlist'
                          ? 'Warteliste'
                          : 'Unbekannt'}
                </span>
              </div>
            </div>
          </div>

          {/* Boolean Values Section */}
          <div className="mb-6 rounded-lg bg-gray-50 p-4">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Anmeldung-Details</h3>
              <button
                onClick={handleLogPaymentStatus}
                disabled={isLoadingPaymentStatus}
                className="rounded-md border border-purple-200 bg-purple-50 px-3 py-1 text-sm font-medium text-purple-600 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {isLoadingPaymentStatus ? 'Lädt...' : 'Zahlungsstatus loggen'}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Leihausrüstung benötigt:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.needsEquipment
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {anmeldung.needsEquipment ? 'Ja' : 'Nein'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Liftkarte benötigt:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.needsLiftTicket
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {anmeldung.needsLiftTicket ? 'Ja' : 'Nein'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Interesse Mehrseillängen:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.interestMultiPitch
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {anmeldung.interestMultiPitch ? 'Ja' : 'Nein'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Foto-Einverständnis:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.photoConsent
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {anmeldung.photoConsent ? 'Erteilt' : 'Nicht erteilt'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Haftungsausschluss:</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    anmeldung.liabilityConsent
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {anmeldung.liabilityConsent ? 'Akzeptiert' : 'Nicht akzeptiert'}
                </span>
              </div>
            </div>
          </div>

          {/* Children Management Section - Only for Family Camps */}
          <div className="rounded-lg bg-gray-50 p-4">
            {anmeldung?.camp && (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">Kinder</h3>
                  <button
                    onClick={handleAddChild}
                    className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Kind hinzufügen
                  </button>
                </div>

                {children.length === 0 ? (
                  <p className="text-sm italic text-gray-500">Keine Kinder registriert</p>
                ) : (
                  <div className="space-y-2">
                    {children.map((child, index) => (
                      <div key={index} className="rounded-md border border-gray-200 bg-white p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4 text-sm">
                              <span className="font-medium text-gray-900">{child.name}</span>
                              <span className="text-gray-500">
                                {child.birthday
                                  ? `${calculateAge(child.birthday)} Jahre`
                                  : 'Kein Geburtstag'}
                              </span>
                              {child.needsLiftTicket && (
                                <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                                  Ticket
                                </span>
                              )}
                              {child.needsEquipment && (
                                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                                  Leihausrüstung
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditChild(index)}
                              className="border border-blue-200 bg-blue-100 text-sm text-blue-600 hover:cursor-pointer hover:bg-blue-200"
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => handleDeleteChild(index)}
                              className="border border-red-200 bg-red-100 text-sm text-red-600 hover:cursor-pointer hover:bg-red-200"
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-4">
            {/* Camp Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Camp ändern</label>
              <select
                value={selectedCampId}
                onChange={(e) => setSelectedCampId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectableCamps.map((camp) => (
                  <option key={camp.id} value={camp.id}>
                    {formatCampTitleYearCategory(camp)}
                  </option>
                ))}
              </select>
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={showPastCamps}
                  onChange={(e) => setShowPastCamps(e.target.checked)}
                />
                Vergangene Camps anzeigen
              </label>
              {selectedCampId !== anmeldung.camp.id && (
                <div className="flex justify-end">
                  <button
                    onClick={handleCampChange}
                    className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Camp ändern bestätigen
                  </button>
                </div>
              )}
            </div>

            {/* Alternative Camp Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Alternative Camp
              </label>
              <select
                value={selectedAlternativeCampId}
                onChange={(e) => setSelectedAlternativeCampId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Kein alternatives Camp</option>
                {selectableCamps
                  .filter((camp) => camp.id !== anmeldung.camp.id) // Exclude current camp
                  .map((camp) => (
                    <option key={camp.id} value={camp.id}>
                      {formatCampTitleYearCategory(camp)}
                    </option>
                  ))}
              </select>
            </div>

            {/* Status Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Notizen (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Interne Notizen zu dieser Anmeldung..."
                rows={3}
                className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Camp Change Confirmation Dialog */}
      {showCampChangeConfirmation && (
        <div className="z-60 fixed inset-0 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowCampChangeConfirmation(false)}
          />

          <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Camp ändern bestätigen</h3>

              <div className="mb-4">
                <p className="mb-2 text-base text-gray-600">
                  <strong>Von:</strong> {formatCampTitleYearCategory(anmeldung.camp)}
                </p>
                <p className="mb-4 text-base text-gray-600">
                  <strong>Zu:</strong>{' '}
                  {selectedCampChangeTarget
                    ? formatCampTitleYearCategory(selectedCampChangeTarget)
                    : ''}
                </p>
                <p className="mb-4 text-base text-red-600">
                  Der Teilnehmer wird automatisch in das neue Camp übertragen.
                </p>
              </div>

              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Notizen zur Camp-Änderung (optional)
                </label>
                <textarea
                  value={campChangeNotes}
                  onChange={(e) => setCampChangeNotes(e.target.value)}
                  placeholder="Grund für die Camp-Änderung..."
                  rows={3}
                  className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCampChangeConfirmation(false)}
                  disabled={isLoading}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCampChangeConfirm}
                  disabled={isLoading}
                  className="rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  {isLoading ? 'Wird geändert...' : 'Camp ändern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Child Form Modal */}
      {showChildForm && (
        <div className="z-60 fixed inset-0 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelChild} />

          <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                {editingChildIndex !== null ? 'Kind bearbeiten' : 'Kind hinzufügen'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={childForm.name}
                    onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Name des Kindes"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Geburtstag *
                  </label>
                  <input
                    type="date"
                    value={childForm.birthday}
                    max={
                      new Date(
                        new Date().getFullYear() - 6,
                        new Date().getMonth(),
                        new Date().getDate(),
                      )
                        .toISOString()
                        .split('T')[0]
                    }
                    onChange={(e) => setChildForm({ ...childForm, birthday: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Geburtstag (TT.MM.JJJJ)"
                  />
                  {childForm.birthday && (
                    <p className="mt-1 text-sm text-gray-500">
                      Alter: {calculateAge(childForm.birthday)} Jahre
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={childForm.needsLiftTicket}
                      onChange={(e) =>
                        setChildForm({ ...childForm, needsLiftTicket: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Benötigt Ticket</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={childForm.needsEquipment}
                      onChange={(e) =>
                        setChildForm({ ...childForm, needsEquipment: e.target.checked })
                      }
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Benötigt Leihausrüstung</span>
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleCancelChild}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveChild}
                  disabled={!childForm.name.trim() || !childForm.birthday}
                  className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {editingChildIndex !== null ? 'Aktualisieren' : 'Hinzufügen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
